import {
  readGrandTotal,
  readGrandTotalOrZero,
} from 'components/insights/careTransitions/shared/slideout/rollupHelpers';
import { StatTile, TilesGrid } from 'components/insights/careTransitions/shared/slideout/statTiles';
import { Metrics } from 'components/insights/constants';
import { formatDaysValue, formatPercentage, formatWholeNumber } from 'components/insights/formatting';
import AnalyticsResponseRollupCollection from 'components/insights/query/AnalyticsResponseRollupCollection';
import { useBusinessDevelopmentStore } from 'stores/businessDevelopmentStore';

import { TABLE_PIVOT, TablePivot } from '../constants';

type Props = {
  careTransitionsRollups: AnalyticsResponseRollupCollection;
  dischargesRollups: AnalyticsResponseRollupCollection;
  referralsRollups: AnalyticsResponseRollupCollection;
  tablePivot: TablePivot;
  loading: boolean;
};

export default function SlideoutTiles({
  careTransitionsRollups,
  dischargesRollups,
  referralsRollups,
  tablePivot,
  loading,
}: Props) {
  const groupTypeName = useBusinessDevelopmentStore((s) => s.selectedGroupType!.displayName);

  if (tablePivot === TABLE_PIVOT.DISCHARGES) {
    const dischargeCount = readGrandTotalOrZero(dischargesRollups, Metrics.ID_COUNT);
    const readmissionRate = readGrandTotal(dischargesRollups, Metrics.READMISSION_RATE);

    return (
      <TilesGrid $columns={2}>
        <StatTile label={`${groupTypeName} Discharges`} primary={formatWholeNumber(dischargeCount)} loading={loading} />
        <StatTile
          label={`${groupTypeName} Readmission Rate`}
          primary={formatPercentage(readmissionRate)}
          loading={loading}
        />
      </TilesGrid>
    );
  }

  if (tablePivot === TABLE_PIVOT.REFERRALS) {
    const referrals = readGrandTotalOrZero(referralsRollups, Metrics.REVIEW_ID_COUNT);
    const acceptanceRate = readGrandTotal(referralsRollups, Metrics.ACCEPTANCE_RATE);
    const denialRate = readGrandTotal(referralsRollups, Metrics.DENIAL_RATE);

    return (
      <TilesGrid $columns={3}>
        <StatTile label={`${groupTypeName} Referrals`} primary={formatWholeNumber(referrals)} loading={loading} />
        <StatTile
          label={`${groupTypeName} Acceptance Rate`}
          primary={formatPercentage(acceptanceRate)}
          tone='green'
          loading={loading}
        />
        <StatTile
          label={`${groupTypeName} Denial Rate`}
          primary={formatPercentage(denialRate)}
          tone='red'
          loading={loading}
        />
      </TilesGrid>
    );
  }

  // Default: patientVolume
  const volume = readGrandTotalOrZero(careTransitionsRollups, Metrics.LOCATION_EPISODE_ID_COUNT);
  const avgLos = readGrandTotal(careTransitionsRollups, Metrics.LENGTH_OF_STAY_AVERAGE);

  return (
    <TilesGrid $columns={2}>
      <StatTile label={`${groupTypeName} Volume`} primary={formatWholeNumber(volume)} loading={loading} />
      <StatTile label={`${groupTypeName} Avg Length of Service`} primary={formatDaysValue(avgLos)} loading={loading} />
    </TilesGrid>
  );
}
